import type { AccessResult, ProviderAccessView } from "../../api/providerAccess";

/** The conflict reasons the server names (`ProviderAccessReasons`); anything else gets the generic conflict text. */
const knownConflictReasons = new Set([
  "ProviderNotEnabled",
  "NoVendorIdentity",
  "FlaggedForDeletion",
  "Paused",
  "AcknowledgedKeyExists",
  "NoUnacknowledgedKey",
  "NoActiveAcknowledgedKey",
  "BothKeysActive",
  "NoKeyToRevoke",
  "NotFlagged",
  "ProviderStillEnabled",
]);

/**
 * The translation key for a refused provider-access call, from its status and `reason` code. The text behind each key is fixed
 * and generic: an engineer is never shown a vendor's words or a manual task's reason, so the server's `message` is not used here.
 * An unknown reason falls back to a safe sentence rather than crashing.
 */
export function accessProblemKey(result: Exclude<AccessResult<unknown>, { kind: "ok" } | { kind: "notFound" }>): string {
  switch (result.kind) {
    case "conflict":
      return result.reason !== null && knownConflictReasons.has(result.reason)
        ? `providerAccess.problems.conflict.${result.reason}`
        : "providerAccess.problems.conflict.other";
    case "denied":
      return "providerAccess.problems.notEnabled";
    case "paused":
      if (result.reason === "ChangeBoundReached") {
        return "providerAccess.problems.changeBound";
      }
      return result.reason === "ProviderWritesOff" ? "providerAccess.problems.writesOff" : "providerAccess.problems.paused";
    case "failed":
      return "providerAccess.problems.failed";
    case "error":
      return "providerAccess.problems.error";
  }
}

/** The sentence that says where a person stands; unknown statuses and reasons fall back to a generic one. */
export function accessStatusKey(view: ProviderAccessView): string {
  switch (view.status) {
    case "BeingSetUp":
      return "providerAccess.status.beingSetUp";
    case "Active":
      return "providerAccess.status.active";
    case "Restricted":
      if (view.reason === "WaitingForAllowance") {
        return "providerAccess.status.waitingForAllowance";
      }

      return view.reason === "AllowanceUsedUp" ? "providerAccess.status.allowanceUsedUp" : "providerAccess.status.restricted";
    case "Paused":
      return "providerAccess.status.paused";
    case "SwitchedOff":
      return view.reason === "KeyExpired" ? "providerAccess.status.keyExpired" : "providerAccess.status.switchedOff";
    default:
      return "providerAccess.status.unknown";
  }
}

/**
 * The text for a refused administrator call: the server's own words for a conflict or a vendor failure (an administrator may see
 * them), otherwise the fixed text for the problem.
 */
export function adminProblemText(result: Exclude<AccessResult<unknown>, { kind: "ok" } | { kind: "notFound" }>, t: (key: string) => string): string {
  if ((result.kind === "conflict" || result.kind === "failed") && result.message !== "") {
    return result.message;
  }

  // An administrator can act on the writes-off refusal, so is told where to turn writes on; an engineer is not (accessProblemKey).
  if (result.kind === "paused" && result.reason === "ProviderWritesOff") {
    return t("providerAccess.admin.writesOff");
  }

  return t(accessProblemKey(result));
}

/** The access statuses the server names (`ProviderAccessStatuses`). */
const knownStatuses = new Set(["NotRequested", "BeingSetUp", "Active", "Restricted", "Paused", "SwitchedOff"]);

/** The reasons the server gives with a status or a deletion flag (`ProviderAccessStatuses`, `ProviderAccessFlagReasons`). */
const knownReasons = new Set(["AllowanceUsedUp", "WaitingForAllowance", "SwitchedOff", "KeyExpired"]);

/** The translation key for an access status in an administrator's view; an unknown status gets a generic word, never the raw code. */
export function adminStatusKey(status: string): string {
  return knownStatuses.has(status) ? `providerAccess.admin.statuses.${status}` : "providerAccess.admin.statuses.unknown";
}

/** The translation key for a status or flag reason in an administrator's view; an unknown reason gets a generic word, never the raw code. */
export function adminReasonKey(reason: string): string {
  return knownReasons.has(reason) ? `providerAccess.admin.reasons.${reason}` : "providerAccess.admin.reasons.unknown";
}
